/**
 * `ChatComposer` — the bottom prompt input. Owns the composer's local
 * state (text, files, dictation, queued submissions) and forwards every
 * actual send through `onSubmit`.
 *
 * Queued messages render above the input so the user can see what's
 * pending while the previous turn is still streaming.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { FileUIPart } from "ai";
import { AudioLinesIcon, MicIcon, XIcon } from "lucide-react";
import {
  PromptInput,
  PromptInputAttachmentButton,
  PromptInputAttachments,
  PromptInputEditor,
  PromptInputSubmit,
  PromptInputToolbar,
  cn,
  filesToFileUIParts,
} from "@flint/ui";
import type { QueuedComposerMessage } from "./types.ts";

type ChatStatus = "submitted" | "streaming" | "ready" | "error";

interface ChatComposerProps {
  status: ChatStatus;
  loadingThread: boolean;
  onSubmit: (message: { text: string; files: FileUIPart[] }) => void;
  onStop: () => void;
  /** Show the paperclip / drag-drop / file-paste path. Default true. */
  showAttachments?: boolean;
  /** Show the mic / dictation button. Default true. */
  showVoice?: boolean;
  placeholder?: string;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
}

interface SpeechRecognitionEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

export function ChatComposer({
  status,
  loadingThread,
  onSubmit,
  onStop,
  showAttachments = true,
  showVoice = true,
  placeholder = "Type your prompt — / for patterns, $ for skills, @ to search files",
}: ChatComposerProps) {
  const [input, setInput] = useState("");
  const [files, setFiles] = useState<FileUIPart[]>([]);
  const [queued, setQueued] = useState<QueuedComposerMessage[]>([]);
  const [isDictating, setIsDictating] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  async function handleFiles(newFiles: File[]) {
    const parts = await filesToFileUIParts(newFiles);
    setFiles((current) => [...current, ...parts]);
  }

  const isBusy = status === "submitted" || status === "streaming";
  const speechRecognitionAvailable = useMemo(() => getSpeechRecognitionConstructor() !== null, []);

  // Drain one queued message per idle tick so the user can type-ahead
  // without losing input while a turn is in flight.
  useEffect(() => {
    if (isBusy || queued.length === 0) return;
    const [next, ...rest] = queued;
    if (!next) return;
    setQueued(rest);
    onSubmit({ text: next.text, files: next.files });
  }, [isBusy, queued, onSubmit]);

  function clearComposer() {
    setInput("");
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function submit(message: { text: string; files: FileUIPart[] }) {
    const text = message.text.trim();
    if (!text && message.files.length === 0) return;

    if (isBusy) {
      setQueued((current) => [...current, { id: crypto.randomUUID(), text, files: message.files }]);
      clearComposer();
      return;
    }
    onSubmit({ text, files: message.files });
    clearComposer();
  }

  function toggleDictation() {
    if (isDictating) {
      recognitionRef.current?.stop();
      setIsDictating(false);
      return;
    }
    const SpeechRecognition = getSpeechRecognitionConstructor();
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-IN";
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript ?? "")
        .join(" ")
        .trim();
      if (transcript) {
        setInput((current) => `${current}${current ? " " : ""}${transcript}`);
      }
    };
    recognition.onend = () => setIsDictating(false);
    recognition.onerror = () => setIsDictating(false);
    recognitionRef.current = recognition;
    setIsDictating(true);
    recognition.start();
  }

  return (
    <div className="shrink-0">
      <div
        className="mx-auto w-full max-w-full px-3 pb-2 pt-1 lg:max-w-3xl lg:px-4 xl:max-w-4xl"
        {...(showAttachments
          ? {
              onDragOver: (e) => {
                if (!e.dataTransfer.types.includes("Files")) return;
                e.preventDefault();
                setIsDragOver(true);
              },
              onDragLeave: (e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                setIsDragOver(false);
              },
              onDrop: async (e) => {
                e.preventDefault();
                setIsDragOver(false);
                const dropped = Array.from(e.dataTransfer.files);
                if (dropped.length > 0) void handleFiles(dropped);
              },
            }
          : {})}
      >
        {queued.length > 0 ? (
          <div className="mb-2 space-y-2">
            {queued.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 rounded-md border border-line bg-paper px-3 py-2 text-xs text-ink-2 shadow-sm"
              >
                <span className="font-medium text-ink">Queued next</span>
                <span className="min-w-0 flex-1 truncate">
                  {item.text || `${item.files.length} attachment(s)`}
                </span>
                <button
                  type="button"
                  className="grid h-6 w-6 place-items-center rounded-md hover:bg-bg"
                  onClick={() =>
                    setQueued((current) => current.filter((entry) => entry.id !== item.id))
                  }
                  aria-label="Remove queued message"
                >
                  <XIcon className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {/* White composer card with a thin line border + soft shadow —
            no gray ring around it. Matches the rest of Flint's
            elevated surfaces (invoice cards, etc.) so the chat input
            doesn't feel like a special-snowflake.

            On focus we deepen the border to sky and lift the shadow
            via a `transition-all` so the user clearly sees the input
            is armed. `focus-within:` lets the styling react to focus
            anywhere inside (TipTap editor, attachment button…). */}
        <PromptInput
          className={cn(
            "w-full overflow-hidden rounded-2xl border border-line bg-paper shadow-card",
            "transition-all duration-200 ease-out",
            isDragOver
              ? "border-sky-400 shadow-lg ring-2 ring-sky-100"
              : "focus-within:border-sky-400 focus-within:shadow-lg focus-within:ring-2 focus-within:ring-sky-100",
          )}
          message={{ text: input, files }}
          onSubmit={(message, event) => {
            event.preventDefault();
            submit({ text: message.text, files: message.files });
          }}
        >
          <div className="overflow-hidden rounded-2xl bg-paper text-base">
            {showAttachments ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="pointer-events-none fixed -left-4 -top-4 size-0.5 opacity-0"
                  accept="image/*,.pdf,.csv,.xlsx,.xls,.txt"
                  onChange={(event) => {
                    const selected = Array.from(event.currentTarget.files ?? []);
                    void handleFiles(selected);
                  }}
                />
                <PromptInputAttachments
                  files={files}
                  onRemove={(file) =>
                    setFiles((current) => current.filter((entry) => entry !== file))
                  }
                />
              </>
            ) : null}
            <div className="px-3 py-0.5">
              <PromptInputEditor
                value={input}
                onValueChange={setInput}
                onSubmit={() => submit({ text: input, files })}
                disabled={loadingThread || isDictating}
                placeholder={placeholder}
                {...(showAttachments ? { onFilePaste: handleFiles } : {})}
              />
            </div>
            <PromptInputToolbar className="border-t-0 px-3 py-1.5">
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {showAttachments ? (
                  <PromptInputAttachmentButton
                    inputRef={fileInputRef}
                    className="h-8 w-8 rounded-full border-0"
                    disabled={loadingThread}
                  />
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1.5 text-ink-2">
                {showVoice ? (
                  <button
                    type="button"
                    onClick={toggleDictation}
                    disabled={!speechRecognitionAvailable || loadingThread}
                    className={cn(
                      "grid h-8 w-8 place-items-center rounded-full transition-colors hover:bg-bg hover:text-ink disabled:opacity-50",
                      isDictating && "bg-sky-50 text-sky-700",
                    )}
                    title={
                      speechRecognitionAvailable
                        ? "Start voice recording"
                        : "Voice recording is not available"
                    }
                    aria-label={isDictating ? "Stop voice recording" : "Start voice recording"}
                  >
                    {isDictating ? (
                      <AudioLinesIcon className="h-4 w-4" aria-hidden />
                    ) : (
                      <MicIcon className="h-4 w-4" aria-hidden />
                    )}
                  </button>
                ) : null}
                <PromptInputSubmit
                  className="h-8 w-8 rounded-lg"
                  status={status}
                  onStop={onStop}
                  disabled={loadingThread || (!isBusy && !input.trim() && files.length === 0)}
                />
              </div>
            </PromptInputToolbar>
          </div>
        </PromptInput>
        <p className="mt-1.5 w-full text-center text-xs leading-4 text-muted">
          Chat output is generated by AI and may not always be accurate. Please verify the
          information before taking any action.
        </p>
      </div>
    </div>
  );
}

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;
  const candidate = window as typeof window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return candidate.SpeechRecognition ?? candidate.webkitSpeechRecognition ?? null;
}
